import React, { useState } from 'react';

import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';

import { AddContainerImageVersionRequest, EnvironmentClient } from '../../../api/api';

interface AddVersionDialogProps {
	open: boolean;
	imageId: string;
	imageName: string;
	onClose: () => void;
	onVersionAdded: () => void;
}

export default function AddVersionDialog({ open, imageId, imageName, onClose, onVersionAdded }: AddVersionDialogProps) {
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const toastService = useToastService();
	const [version, setVersion] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleClose = () => {
		if (!isSubmitting) {
			setVersion('');
			setError(null);
			onClose();
		}
	};

	const handleSubmit = async () => {
		// Validate version
		if (!version.trim()) {
			setError('Version is required');
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			const request: AddContainerImageVersionRequest = {
				version: version.trim(),
			};

			await client.addVersion(imageId, request);
			toastService.success(`Version "${version.trim()}" added successfully to ${imageName}`);
			setVersion('');
			onVersionAdded();
		} catch (err) {
			console.error('Error adding version:', err);
			const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
			setError(`Failed to add version: ${errorMessage}`);
			toastService.error(`Failed to add version: ${errorMessage}`);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !isSubmitting) {
			handleSubmit();
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Add New Version</DialogTitle>
			<DialogContent>
				<Box sx={{ pt: 2 }}>
					<Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
						Add a new version to container image: <strong>{imageName}</strong>
					</Typography>

					{error && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{error}
						</Alert>
					)}

					<TextField
						autoFocus
						fullWidth
						label="Version"
						placeholder="e.g., 1.0.0, v2.3.1, latest"
						value={version}
						onChange={(e) => {
							setVersion(e.target.value);
							setError(null);
						}}
						onKeyPress={handleKeyPress}
						disabled={isSubmitting}
						helperText="Enter the version tag for this container image"
						error={!!error}
					/>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={handleClose} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					color="primary"
					disabled={isSubmitting || !version.trim()}
					startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
				>
					{isSubmitting ? 'Adding...' : 'Add Version'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
